import { api } from './client';

export interface CashbackTier {
  min_brl: string;
  max_brl: string | null;
  percent: number;
  label?: string;
  description?: string;
}

export interface CashbackRuleOut {
  version: number;
  tiers: CashbackTier[];
  valid_from: string;
  valid_until: string | null;
  promo_title?: string;
  promo_description?: string;
  // Fator 0..1 por trilho de pagamento (migration 051). null/ausente = regra
  // anterior ao cartão → o front aplica {pix:1, credit_card:0}. É o que faz o
  // preview do cartão bater com o crédito real (cashback zero no dia 1).
  method_factors?: { pix?: string; credit_card?: string } | null;
}

/** Fator do trilho (0..1) — espelha `method_factor` do backend. Método
 *  desconhecido ou regra sem factors → default (pix 1, cartão 0). */
export function methodFactor(
  rule: CashbackRuleOut | null,
  method: 'pix' | 'credit_card'
): number {
  const raw = rule?.method_factors?.[method];
  if (raw != null) {
    const n = Number(raw);
    return Number.isFinite(n) ? Math.min(Math.max(n, 0), 1) : method === 'pix' ? 1 : 0;
  }
  return method === 'pix' ? 1 : 0;
}

export const promotionsApi = {
  getActiveCashbackRule: () =>
    api.get<CashbackRuleOut>('/promotions/cashback/tiers/active')
};

/** Seleciona o tier correspondente ao valor (maior `min_brl` <= amount). */
export function selectTier(
  amount: number,
  tiers: CashbackTier[]
): CashbackTier | null {
  const sorted = [...tiers].sort(
    (a, b) => Number(b.min_brl) - Number(a.min_brl)
  );
  return sorted.find((t) => amount >= Number(t.min_brl)) ?? null;
}

/** Próximo tier (se houver) — útil pra incentivar upgrade de valor. */
export function nextTier(
  amount: number,
  tiers: CashbackTier[]
): CashbackTier | null {
  const sorted = [...tiers].sort(
    (a, b) => Number(a.min_brl) - Number(b.min_brl)
  );
  return sorted.find((t) => Number(t.min_brl) > amount) ?? null;
}

/**
 * Calcula o cashback esperado — espelha `compute_cashback` do backend
 * (`floor_money`: arredonda pra baixo, nunca paga centavo a mais).
 */
export function expectedCashback(amount: number, tier: CashbackTier | null): number {
  if (!tier) return 0;
  const raw = amount * (tier.percent / 100);
  return Math.floor(raw * 100) / 100;
}

export function tierName(percent: number): string {
  if (percent >= 15) return 'Pleno';
  if (percent >= 7) return 'Turbo';
  if (percent >= 3) return 'Raio';
  return 'Sem tier';
}
